import type { IconName } from '../../../../components/ui/Icon';

/**
 * 管理后台的导航数据。左侧两列都走 `ModuleSidebar` 的 `appearance="admin"`：
 * 第一列是分组（`adminGroups`），第二列是当前功能自己的二级菜单（`AdminNavItem.sections`）。
 *
 * 分组图标使用 2026-09-24 用户提供的 Iconfont 图标包。
 */

export interface AdminSubItem {
  id: string;
  label: string;
}

export interface AdminSubSection {
  id: string;
  items: readonly AdminSubItem[];
  title: string;
}

export interface AdminNavItem {
  id: string;
  label: string;
  /** 该功能的二级菜单；没有就不渲染第二列。 */
  sections?: readonly AdminSubSection[];
}

export interface AdminNavGroup {
  icon?: IconName;
  id: string;
  items: readonly AdminNavItem[];
  label: string;
}

export const adminOverview = { id: 'overview', label: '概览', icon: 'icon' } as const;

export const adminGroups: readonly AdminNavGroup[] = [
  {
    icon: 'icon-1',
    id: 'organization',
    label: '机构管理',
    items: [
      { id: 'campuses', label: '校区管理' },
      { id: 'employees', label: '员工管理' },
      { id: 'permissions', label: '权限管理' },
      { id: 'course-authorization', label: '课程授权' },
      { id: 'cameras', label: '摄像头管理' },
      { id: 'classrooms', label: '教室管理' },
    ],
  },
  {
    icon: 'icon-2',
    id: 'business',
    label: '业务设置',
    items: [
      { id: 'fields', label: '字段管理' },
      { id: 'rules', label: '业务规则' },
      { id: 'approvals', label: '审批管理' },
      { id: 'more-settings', label: '更多设置' },
    ],
  },
  {
    icon: 'icon-3',
    id: 'system',
    label: '系统设置',
    items: [
      { id: 'menus', label: '菜单管理' },
      { id: 'print-templates', label: '打印模板' },
      {
        id: 'data-import',
        label: '数据导入',
        sections: [
          {
            id: 'recruitment',
            title: '招生营销',
            items: [
              { id: 'intention-students', label: '意向学员数据' },
              { id: 'communication-records', label: '沟通记录数据' },
            ],
          },
          {
            id: 'front-desk',
            title: '前台业务',
            items: [
              { id: 'student-points', label: '学员积分数据' },
              { id: 'student-fees', label: '学员费用数据' },
              { id: 'order-import', label: '订单导入' },
            ],
          },
          {
            id: 'education',
            title: '教务管理',
            items: [
              { id: 'course-data', label: '课程数据' },
              { id: 'class-data', label: '班级数据' },
              { id: 'split-class-data', label: '分班数据' },
              { id: 'schedule-data', label: '排课数据' },
              { id: 'public-school-data', label: '公办学校数据' },
              { id: 'student-housing-data', label: '学员入住数据' },
            ],
          },
          {
            id: 'organization',
            title: '机构',
            items: [{ id: 'staff-data', label: '员工数据' }],
          },
        ],
      },
      { id: 'operation-logs', label: '操作日志' },
      { id: 'system-information', label: '系统信息' },
    ],
  },
  {
    icon: 'icon-4',
    id: 'messages',
    label: '消息中心',
    items: [
      { id: 'message-management', label: '消息管理' },
      { id: 'sms-credit', label: '短信充值' },
    ],
  },
  {
    icon: 'icon1',
    id: 'integrations',
    label: '第三方管理',
    items: [
      { id: 'third-party-platforms', label: '第三方平台' },
      { id: 'open-platform', label: '开放平台' },
      { id: 'aira', label: 'AIRA数字员工' },
    ],
  },
];

export function findAdminItem(id: string | null | undefined, groups: readonly AdminNavGroup[] = adminGroups) {
  if (!id) return undefined;
  for (const group of groups) {
    const item = group.items.find((candidate) => candidate.id === id);
    if (item) return { group, item };
  }
  return undefined;
}
